import { useState, useEffect, useRef, useCallback } from "react";
import PublishDialog from "../PublishDialog";

export default function EditorStep({
  videoUrl,
  videoPath,
  topic,
  videoId,
  onCreateAnother,
  onDownload,
}) {
  const [videoDuration, setVideoDuration] = useState(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const videoRef = useRef(null);
  
  const videoSrc = videoUrl || videoPath || null;
  const fileName = videoUrl ? "video.mp4" : (videoPath ? videoPath.split("/").pop() : "video.mp4");

  useEffect(() => {
    if (videoRef.current && videoSrc) {
      videoRef.current.load();
    }
  }, [videoSrc]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current && isFinite(videoRef.current.duration)) {
      setVideoDuration(videoRef.current.duration);
    }
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds) return "--:--";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-28 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Video Player */}
          <div>
            <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-lg">
              {videoSrc ? (
                <video
                  ref={videoRef}
                  key={videoSrc}
                  controls
                  className="w-full aspect-video"
                  poster=""
                  onLoadedMetadata={handleLoadedMetadata}
                >
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full aspect-video flex items-center justify-center bg-gray-800">
                  <span className="text-gray-500 text-sm">Video preview</span>
                </div>
              )}
            </div>

            {/* Ready badge */}
            <div className="flex items-center gap-2 mt-4 px-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-green-600 font-medium">Video is ready</span>
            </div>
          </div>

          {/* Info & Actions */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Your video is ready
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              Download your AI-generated video in full HD quality, or start a new project.
            </p>

            {/* File info card */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{fileName}</p>
                  <p className="text-xs text-gray-500">1080p Full HD</p>
                </div>
                <span className="px-2 py-1 text-[10px] font-semibold text-primary-600 bg-primary-50 rounded-md uppercase tracking-wider">
                  Optimized
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-1">Duration</p>
                  <p className="text-sm font-medium text-gray-800">{formatDuration(videoDuration)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-1">Format</p>
                  <p className="text-sm font-medium text-gray-800">H.264 / MP4</p>
                </div>
              </div>
            </div>

            {/* Download button */}
            <a
              href={videoSrc}
              download={fileName}
              onClick={onDownload}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors mb-3 shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Video
            </a>

            {/* Secondary actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={onCreateAnother}
                className="flex items-center justify-center gap-2 py-3 min-h-[44px] bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Create Another
              </button>
              <button
                onClick={() => setShowPublishDialog(true)}
                className="flex items-center justify-center gap-2 py-3 min-h-[44px] bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                </svg>
                Publish
              </button>
            </div>
          </div>
        </div>
      </div>

      <PublishDialog
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        videoId={videoId}
        videoUrl={videoUrl || videoPath}
        topic={topic}
      />

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-500 truncate">
            Project: {topic}
          </span>
          <div className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            Ready
          </div>
        </div>
      </div>
    </div>
  );
}
